import { TagSkeleton } from '@carbon/react';
import { StatsBar, type StatItem } from 'components/stats-bar';
import type { CleanupLogStats } from './types';
import shared from '../admin-log.module.scss';

interface CleanupStatsProps {
  // Undefined until the first page of the log arrives, or when it failed.
  stats: CleanupLogStats | undefined;
  loading: boolean;
}

/**
 * Individual + Organization are the library; Imported is provisional
 * throughput, kept on its own tile so it is never read as progress against
 * the library-cleanliness number.
 * @param {CleanupStatsProps} props - The counts for the current filters
 * @return {JSX.Element | null} The stats strip and its note, a skeleton while
 * the first counts load, or nothing when they failed (the table shows why)
 */
export function CleanupStats({ stats, loading }: CleanupStatsProps) {
  if (!stats) {
    if (!loading) return null;
    return (
      <div
        className={shared.statsHeader}
        role="status"
        aria-label="Loading counts"
      >
        <div className={shared.skeletonTiles}>
          {['library', 'individual', 'organization', 'imported'].map((tile) => (
            <TagSkeleton key={tile} />
          ))}
        </div>
      </div>
    );
  }

  const items: StatItem[] = [
    { label: 'Library retired', value: stats.library, tagType: 'blue' },
    { label: 'Individuals', value: stats.individual, tagType: 'teal' },
    { label: 'Organizations', value: stats.organization, tagType: 'purple' },
    { label: 'Imported retired', value: stats.imported, tagType: 'gray' },
  ];

  return (
    <div className={shared.statsHeader}>
      <StatsBar stats={items} />
      <p className={shared.statsNote}>
        Counts follow the filters below. Imported entities are not part of the
        library count.
      </p>
    </div>
  );
}
